import React, { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  Button,
  Space,
  message,
  Typography,
  Alert,
  Upload,
  PictureOutlined,
  DeleteOutlined,
} from "@mimber/ui-web"
import { Form, ColorPickerField } from "@mimber/forms"
import {
  useUpdateOrganizationProfile,
  useUploadOrganizationLogo,
  useDeleteOrganizationLogo,
} from "@mimber/client/mimber-organization"
import { Image } from "antd"
import type { UploadFile } from "antd"
import type { OrganizationBrandingFormProps } from "./OrganizationBrandingForm.types"
import {
  organizationBrandingSchema,
  type OrganizationBrandingFormData,
} from "./OrganizationBrandingForm.schema"

const { Paragraph, Text } = Typography

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml"]

type LogoAction = "none" | "upload" | "delete"

export const OrganizationBrandingForm: React.FunctionComponent<
  OrganizationBrandingFormProps
> = ({ organization, onSuccess, disabled = false }) => {
  const updateProfile = useUpdateOrganizationProfile(organization.uuid)
  const uploadLogo = useUploadOrganizationLogo(organization.uuid)
  const deleteLogo = useDeleteOrganizationLogo(organization.uuid)

  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [logoAction, setLogoAction] = useState<LogoAction>("none")
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const form = useForm<OrganizationBrandingFormData>({
    defaultValues: {
      primaryColor: organization.primaryColor || "#1890ff",
      secondaryColor: organization.secondaryColor || "#52c41a",
      accentColor: organization.accentColor || "#fa541c",
    },
    resolver: zodResolver(organizationBrandingSchema),
    mode: "onBlur",
  })

  const {
    formState: { isDirty },
    reset,
    watch,
  } = form

  const primaryColor = watch("primaryColor")
  const secondaryColor = watch("secondaryColor")
  const accentColor = watch("accentColor")

  // Clean up preview URL on unmount or when preview changes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  const hasChanges = isDirty || logoAction !== "none"

  const onSubmit = async (data: OrganizationBrandingFormData) => {
    try {
      // Handle logo operations first
      if (logoAction === "delete") {
        await deleteLogo.mutateAsync(undefined)
      } else if (logoAction === "upload" && fileList.length > 0) {
        const file = fileList[0].originFileObj
        if (file) {
          await uploadLogo.mutateAsync({ file })
        }
      }

      // Update profile colors
      const payload = {
        primaryColor: data.primaryColor || null,
        secondaryColor: data.secondaryColor || null,
        accentColor: data.accentColor || null,
      }

      await updateProfile.mutateAsync(payload)

      message.success("Branding updated successfully")
      resetForm()
      onSuccess?.()
    } catch (error: any) {
      message.error(error.message || "Failed to update branding")
    }
  }

  const resetForm = () => {
    reset()
    setFileList([])
    setLogoAction("none")
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
      setPreviewUrl(null)
    }
  }

  const beforeUpload = (file: File) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      message.error("Only PNG, JPG, and SVG files are allowed")
      return Upload.LIST_IGNORE
    }
    if (file.size > MAX_FILE_SIZE) {
      message.error("File must be smaller than 5MB")
      return Upload.LIST_IGNORE
    }
    return false
  }

  const handleChange = (info: { fileList: UploadFile[] }) => {
    const newFileList = info.fileList.slice(-1)
    setFileList(newFileList)

    // Create preview URL for the new file
    if (newFileList.length > 0 && newFileList[0].originFileObj) {
      // Clean up old preview URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }

      const newPreviewUrl = URL.createObjectURL(newFileList[0].originFileObj)
      setPreviewUrl(newPreviewUrl)
      setLogoAction("upload")
    } else {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
      setPreviewUrl(null)
      setLogoAction("none")
    }
  }

  const handleMarkForDeletion = () => {
    setLogoAction("delete")
    setFileList([])
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
      setPreviewUrl(null)
    }
  }

  const handleCancelDeletion = () => {
    setLogoAction("none")
  }

  const handleRemoveFile = () => {
    setFileList([])
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
      setPreviewUrl(null)
    }
    setLogoAction("none")
  }

  const isLoading =
    updateProfile.isPending || uploadLogo.isPending || deleteLogo.isPending

  const currentLogoUrl = organization.logoStorageItem?.storageUrl ?? null
  const showCurrentLogo = currentLogoUrl && logoAction !== "delete"
  const showNewLogoPreview = logoAction === "upload" && previewUrl
  const showDeletionWarning = logoAction === "delete" && currentLogoUrl

  return (
    <div>
      <Paragraph type="secondary" style={{ marginBottom: 24 }}>
        Upload your organization's logo and choose brand colors
      </Paragraph>

      {/* Logo Upload Section */}
      <div style={{ marginBottom: 32 }}>
        <Text
          strong
          style={{ fontSize: 16, marginBottom: 12, display: "block" }}
        >
          <PictureOutlined style={{ marginRight: 8 }} />
          Organization Logo
        </Text>

        <Paragraph type="secondary">
          Upload a logo that represents your organization. This will be
          displayed on your public profile and within the member application.
        </Paragraph>

        {/* Current Logo */}
        {showCurrentLogo && (
          <div style={{ marginBottom: 16 }}>
            <Text
              type="secondary"
              style={{ display: "block", marginBottom: 8 }}
            >
              Current Logo:
            </Text>
            <Space orientation="vertical" align="center">
              <Image
                src={currentLogoUrl}
                alt="Current Organization Logo"
                width={200}
                height={200}
                style={{ objectFit: "contain" }}
              />
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleMarkForDeletion}
                disabled={disabled || isLoading}
              >
                Mark for Deletion
              </Button>
            </Space>
          </div>
        )}

        {/* Deletion Warning */}
        {showDeletionWarning && (
          <Alert
            message="Logo Marked for Deletion"
            description="The current logo will be deleted when you save changes."
            type="warning"
            showIcon
            action={
              <Button
                size="small"
                onClick={handleCancelDeletion}
                disabled={disabled || isLoading}
              >
                Cancel
              </Button>
            }
            style={{ marginBottom: 16 }}
          />
        )}

        {/* New Logo Preview */}
        {showNewLogoPreview && (
          <div style={{ marginBottom: 16 }}>
            <Text
              type="secondary"
              style={{ display: "block", marginBottom: 8 }}
            >
              New Logo (will be uploaded on save):
            </Text>
            <Space orientation="vertical" align="center">
              <Image
                src={previewUrl}
                alt="New Logo Preview"
                width={200}
                height={200}
                style={{ objectFit: "contain" }}
              />
              <Button
                danger
                onClick={handleRemoveFile}
                disabled={disabled || isLoading}
              >
                Remove
              </Button>
            </Space>
          </div>
        )}

        {/* Upload Guidance */}
        {!showNewLogoPreview && (
          <>
            <Alert
              message="Logo Requirements"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>Accepted formats: PNG, JPG, SVG</li>
                  <li>Maximum file size: 5MB</li>
                  <li>Recommended: Square aspect ratio (1:1)</li>
                </ul>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Upload.Dragger
              beforeUpload={beforeUpload}
              onChange={handleChange}
              fileList={fileList}
              maxCount={1}
              disabled={disabled || isLoading}
              showUploadList={false}
            >
              <p className="ant-upload-drag-icon">
                <PictureOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag file to this area to upload
              </p>
              <p className="ant-upload-hint">
                Support for PNG, JPG, and SVG files up to 5MB
              </p>
            </Upload.Dragger>
          </>
        )}
      </div>

      {/* Brand Colors Section */}
      <Form
        form={form}
        onSubmit={onSubmit}
        disabled={disabled || isLoading}
        layout="vertical"
      >
        <Text
          strong
          style={{ fontSize: 16, marginBottom: 12, display: "block" }}
        >
          Brand Colors
        </Text>

        <Paragraph type="secondary">
          Choose colors that represent your organization. These will be applied
          to your public profile.
        </Paragraph>

        <ColorPickerField name="primaryColor" label="Primary Color" />
        <ColorPickerField name="secondaryColor" label="Secondary Color" />
        <ColorPickerField name="accentColor" label="Accent Color" />

        {/* Color Preview */}
        <Alert
          message={
            <Space>
              <Text type="secondary">Preview:</Text>
              {primaryColor && (
                <div
                  style={{
                    width: 40,
                    height: 40,
                    background: primaryColor,
                    borderRadius: 4,
                  }}
                />
              )}
              {secondaryColor && (
                <div
                  style={{
                    width: 40,
                    height: 40,
                    background: secondaryColor,
                    borderRadius: 4,
                  }}
                />
              )}
              {accentColor && (
                <div
                  style={{
                    width: 40,
                    height: 40,
                    background: accentColor,
                    borderRadius: 4,
                  }}
                />
              )}
            </Space>
          }
          type="info"
          showIcon={false}
          style={{ marginTop: 16, marginBottom: 24 }}
        />

        <Space>
          <Button
            type="primary"
            htmlType="submit"
            disabled={disabled || !hasChanges}
            loading={isLoading}
          >
            Save Changes
          </Button>

          <Button
            onClick={resetForm}
            disabled={disabled || !hasChanges || isLoading}
          >
            Reset
          </Button>
        </Space>
      </Form>
    </div>
  )
}
